import { AlertCircle, Home } from 'lucide-react';

import type { InvitationData } from '@/shared/domain/types/auth.types';

interface InvitationDetailsProps {
  invitation: InvitationData;
  onAccept: () => void;
  onSignup: () => void;
  onLogin: () => void;
  error?: string | null;
}

export function InvitationDetails({
  invitation,
  onAccept,
  onSignup,
  onLogin,
  error,
}: InvitationDetailsProps) {
  const isExpired = new Date(invitation.expiresAt) < new Date();

  if (isExpired) {
    return (
      <div className="text-center p-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-neutral-950 mb-2">Invitation Expired</h3>
        <p className="text-neutral-600 mb-4">
          This invitation has expired. Please contact your admin for a new invitation.
        </p>
        <button
          onClick={() => (window.location.href = '/auth/login')}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline"
        >
          ← Back to login
        </button>
      </div>
    );
  }

  return (
    <div className="text-center p-6">
      <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
        <Home className="w-8 h-8 text-green-600" />
      </div>

      <h3 className="text-lg font-semibold text-neutral-950 mb-2">You're Invited!</h3>
      <p className="text-neutral-600 mb-2">{invitation.inviterName} has invited you to join</p>
      <p className="text-xl font-semibold text-neutral-950 mb-2">{invitation.orgName}</p>
      <p className="text-neutral-600 mb-6">
        as a <span className="font-semibold text-blue-600">{invitation.role}</span>
      </p>

      {error && <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">{error}</div>}

      <div className="space-y-3">
        <button
          onClick={onAccept}
          className="w-full h-11 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-300 font-medium"
        >
          Accept Invitation
        </button>

        <div className="text-sm text-neutral-500">or</div>

        <div className="space-y-2">
          <button
            onClick={onSignup}
            className="w-full h-11 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 font-medium"
          >
            Sign Up & Accept
          </button>
          <button
            onClick={onLogin}
            className="w-full h-11 bg-neutral-600 text-white rounded-lg hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 transition-all duration-300 font-medium"
          >
            Login & Accept
          </button>
        </div>
      </div>

      <div className="mt-6 text-xs text-neutral-500">
        Invitation expires on {new Date(invitation.expiresAt).toLocaleDateString()}
      </div>
    </div>
  );
}
